import { computed, inject, Injectable, signal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  MOVEMENT_CAUSE_DEFINITIONS,
  MovementCause,
  MovementEntry,
  NewMovementForm,
  NewMovementFormGroup,
  NewMovementMode,
  NewMovementRowForm,
  NewMovementRowValue,
  SALES_LOCATION_ID,
  STOCK_TRANSFER_CAUSE
} from '../models/stock-new-movement.model';

// un testo digitato nell'autocomplete è valido solo se è stata scelta una voce dell'elenco (md-require-match)
export const requireSelectedOption = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  return typeof value === 'string' && value.trim() !== '' ? { requireMatch: true } : null;
};

const toIsoMinute = (date: Date | string | null | undefined): string => {
  const d = date ? new Date(date) : new Date();
  d.setSeconds(0, 0);
  return d.toISOString();
};

@Injectable()
export class StockNewMovementFormService {

  private readonly translateService = inject(TranslateService);

  private movementForm!: NewMovementFormGroup;

  get form() {
    return this.movementForm;
  }

  readonly formReady = signal(false);
  readonly mode = signal<NewMovementMode | null>(null);

  // titolo dinamico: parte da "Nuovo movimento" e cambia in base al contesto in cui si apre la pagina
  readonly title = computed(() => {
    switch (this.mode()) {
      case 'supplier_order':
        return 'STOCK.NEW_MOVEMENT.NEW_SUPPLIER_ORDER';
      case 'goods_delivery':
      case 'goods_delivery_adjustment':
        return 'STOCK.MOVEMENTS.OPTIONS.GOODS_DELIVERY';
      default:
        return 'STOCK.NEW_MOVEMENT.NEW_MOVEMENT';
    }
  });

  // numero d'ordine proposto (progressivo): si azzera quando si sceglie un fornitore
  defaultSupplierOrder?: string;

  readonly movementCauses: MovementCause[] = MOVEMENT_CAUSE_DEFINITIONS.map(definition => ({
    type: definition.type,
    typeName: definition.typeNameKey ? this.translateService.instant(definition.typeNameKey) : '',
    value: definition.value,
    name: this.translateService.instant(definition.nameKey)
  }));

  createForm(sourceLocationId: number = SALES_LOCATION_ID) {
    this.movementForm = new FormGroup<NewMovementForm>({
      movementCause: new FormControl<MovementCause | null>(null),
      sourceLocationId: new FormControl<number | null>(sourceLocationId),
      targetLocationId: new FormControl<number | null>(null),
      supplier: new FormControl(null, { validators: [requireSelectedOption] }),
      date: new FormControl<string | null>(toIsoMinute(new Date())),
      supplierOrder: new FormControl<string | null>(null),
      notes: new FormControl<string | null>(null),
      rows: new FormArray<FormGroup<NewMovementRowForm>>([])
    });

    this.formReady.set(true);
    return this.movementForm;
  }

  findCause(type: string, value: string): MovementCause | null {
    return this.movementCauses.find(cause => cause.type === type && cause.value === value) ?? null;
  }

  findCauseByValue(value: string): MovementCause | null {
    return this.movementCauses.find(cause => cause.value === value) ?? null;
  }

  compareCauses = (a: MovementCause | null, b: MovementCause | null) => a?.type === b?.type && a?.value === b?.value;

  // nel ricevimento merce di rettifica causale, magazzino e fornitore non sono più modificabili
  setMode(mode: NewMovementMode | null) {
    this.mode.set(mode);

    const locked = mode === 'goods_delivery_adjustment';
    [this.movementCause, this.sourceLocationId, this.supplier].forEach(control => {
      locked ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
    });
  }

  setDate(date: Date | string | null | undefined) {
    this.date.setValue(toIsoMinute(date));
  }

  // ------------------- righe -------------------

  createRow(value: Partial<NewMovementRowValue> = {}) {
    return new FormGroup<NewMovementRowForm>({
      entry: new FormControl<MovementEntry | string | null>(value.entry ?? null),
      quantity: new FormControl<number | null>(value.quantity ?? null),
      price: new FormControl<number | null>(value.price ?? null),
      perc_discount: new FormControl<number | null>(value.perc_discount ?? null),
      oldQuantity: new FormControl<number | null>(value.oldQuantity ?? null)
    });
  }

  addRow(value: Partial<NewMovementRowValue> = {}) {
    this.rows.push(this.createRow(value));
  }

  removeRow(index: number) {
    this.rows.removeAt(index);
  }

  clearRows() {
    this.rows.clear();
  }

  // una riga è vuota se non ha prodotto scelto, quantità, prezzo e sconto
  isRowEmpty(row: NewMovementRowValue): boolean {
    const hasEntry = !!row.entry && typeof row.entry === 'object';
    return !hasEntry && row.quantity == null && row.price == null && row.perc_discount == null;
  }

  removeEmptyRows() {
    for (let i = this.rows.length - 1; i >= 0; i--) {
      if (this.isRowEmpty(this.rows.at(i).getRawValue())) {
        this.rows.removeAt(i);
      }
    }
  }

  hasMovements(): boolean {
    return this.rows.getRawValue().some(row => !this.isRowEmpty(row));
  }

  // ------------------- condizioni dei campi -------------------

  get isStockTransfer(): boolean {
    return this.movementCause.value?.value === STOCK_TRANSFER_CAUSE;
  }

  // il magazzino e la causale sono fissi nell'ordine fornitore e nel ricevimento merce
  get showCauseAndSource(): boolean {
    const mode = this.mode();
    return mode !== 'supplier_order' && mode !== 'goods_delivery';
  }

  get showTargetLocation(): boolean {
    return this.isStockTransfer && this.mode() !== 'goods_delivery';
  }

  get showSupplierFields(): boolean {
    return !this.isStockTransfer || this.mode() === 'goods_delivery';
  }

  // ------------------- controlli -------------------

  get movementCause() {
    return this.movementForm.controls.movementCause;
  }

  get sourceLocationId() {
    return this.movementForm.controls.sourceLocationId;
  }

  get targetLocationId() {
    return this.movementForm.controls.targetLocationId;
  }

  get supplier() {
    return this.movementForm.controls.supplier;
  }

  get date() {
    return this.movementForm.controls.date;
  }

  get supplierOrder() {
    return this.movementForm.controls.supplierOrder;
  }

  get notes() {
    return this.movementForm.controls.notes;
  }

  get rows() {
    return this.movementForm.controls.rows;
  }

  // ------------------- numero d'ordine -------------------

  onSupplierOrderClick() {
    if (this.defaultSupplierOrder && this.defaultSupplierOrder === this.supplierOrder.value) {
      this.supplierOrder.setValue('');
    }
  }

  onSupplierOrderBlur() {
    if (!this.supplierOrder.value) {
      this.supplierOrder.setValue(this.defaultSupplierOrder || '');
    }
  }

  onSupplierSelect() {
    this.defaultSupplierOrder = undefined;
  }
}
